import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  IconButton,
  useTheme,
  alpha,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Snackbar,
  TablePagination,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import {
  Science,
  Analytics,
  CheckCircle,
  Assignment,
  Timer,
  PriorityHigh,
  TrendingUp,
  Assessment,
  Warning,
  Settings,
  BarChart,
  Timeline,
  Tune,
  Add,
  Delete,
  Save,
  PlayArrow,
  Stop,
  Refresh,
  Error,
  Send,
  Visibility,
  Edit,
  AutoAwesome,
  TipsAndUpdates,
  ExpandMore,
  Close,
  ExpandLess,
  LocalFireDepartment,
  VerifiedUser,
  Cancel,
  Search,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import api from '../../services/api';

interface BatchValidationData {
  [sampleId: string]: {
    validationNotes: string;
    approved: boolean;
  };
}

const AUSupervisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // Main state
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [validatingBatch, setValidatingBatch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Batch validation state
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [batchValidationDialog, setBatchValidationDialog] = useState(false);
  const [batchValidationData, setBatchValidationData] = useState<BatchValidationData>({});
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  
  // Individual sample state
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [validationDialog, setValidationDialog] = useState(false);

  // Lot-level validation state (like ICP Supervisor)
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');

  const [stats, setStats] = useState({
    pending_validation: 0,
    validated_today: 0,
    rejected_samples: 0,
    urgent_samples: 0,
  });

  // 🔔 Snackbar state for user feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

  // Partial certificate generation state
  const [generatingPartialCert, setGeneratingPartialCert] = useState(false);
  const [hideWeightInCert, setHideWeightInCert] = useState(false);

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(250); // ~10 batches worth
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalBatches, setTotalBatches] = useState(0);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when search changes
  useEffect(() => {
    loadAUValidationData();
  }, [debouncedSearch]);
    
  // Auto-refresh when NO modals are open
  useEffect(() => {
    // 🔒 CRITICAL FIX: Only refresh when NO modals are open to prevent data corruption during operations
    const interval = setInterval(() => {
      // Don't refresh if any modal is open
      if (!batchValidationDialog && !validationDialog) {
        loadAUValidationData();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [batchValidationDialog, validationDialog, debouncedSearch]);

  // Helper function to get elements for a sample - DYNAMIC based on element_lab_routing
  // 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
  const getElementsForSample = (sample: any): string[] => {
    if (!sample) return ['Au']; // Default fallback
    
    const isBGMSample = sample.metadonnees?.bgm_method_selected === true;
    
    // BGM samples only have Au
    if (isBGMSample) {
      return ['Au'];
    }
    
    // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
    // This ensures elements removed per-sample are properly greyed out
    const selectedElements = sample.metadonnees?.selected_elements || [];
    const elementRouting = sample.metadonnees?.element_lab_routing || {};
    const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true ||
                             Object.keys(elementRouting).length > 0;
    
    if (isMultiLabSample && selectedElements.length > 0) {
      // Filter selected_elements to only those routed to AU lab
      const auElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_AU'
      );
      
      if (auElements.length > 0) {
        console.log(`🔍 AU Supervisor: Elements for ${sample.sample_id} (filtered by selected_elements):`, auElements);
        return auElements;
      }
    }
    
    // Fallback: check selected_elements for Au/Ag (non-multi-lab samples)
    const auElements = selectedElements.filter((el: string) => el === 'Au' || el === 'Ag');
    
    return auElements.length > 0 ? auElements : ['Au'];
  };

  // 🔧 FIX: Get ALL unique elements across entire batch (union of all samples)
  const getAllBatchElements = (batchSamples: any[]): string[] => {
    const allElements = new Set<string>();
    batchSamples.forEach(sample => {
      const sampleElements = getElementsForSample(sample);
      sampleElements.forEach(el => allElements.add(el));
    });
    // Sort: Au first, then Ag, then others alphabetically
    const result = Array.from(allElements).sort((a, b) => {
      if (a === 'Au') return -1;
      if (b === 'Au') return 1;
      if (a === 'Ag') return -1;
      if (b === 'Ag') return 1;
      return a.localeCompare(b);
    });
    return result;
  };

  // 🔍 MULTI-LAB VALIDATION STATUS HELPERS - SAFE VISUAL INDICATORS ONLY (AU PERSPECTIVE)
  const isMultiLabBatch = (batchSamples: Sample[]): boolean => {
    return batchSamples.some(sample => 
      sample.metadonnees?.multi_lab_workflow === true || 
      sample.metadonnees?.bgm_method_selected === true
    );
  };
  
  // Keep old function for backward compatibility
  const isBGMBatch = (batchSamples: Sample[]): boolean => {
    return batchSamples.some(sample => sample.metadonnees?.bgm_method_selected === true);
  };

  const getBGMValidationStatus = (batchSamples: Sample[]) => {
    if (!isMultiLabBatch(batchSamples)) {
      return { isBGM: false, auCompleted: false, icpCompleted: false, awaitingICP: false };
    }

    // Check validation completion status across all samples in batch
    const auCompleted = batchSamples.some(sample => 
      sample.metadonnees?.au_validation_completed === true ||
      sample.metadonnees?.au_validation?.validation_decision === 'APPROVED'
    );
    
    const icpCompleted = batchSamples.some(sample => 
      sample.metadonnees?.icp_validation_completed === true ||
      sample.metadonnees?.icp_validation?.validation_decision === 'APPROVED'
    );

    return {
      isBGM: true,
      auCompleted,
      icpCompleted,
      awaitingICP: auCompleted && !icpCompleted // AU done, waiting for ICP
    };
  };

  const loadAUValidationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🚀 OPTION B (2026-05-06): Replaced legacy fetch+client-side-filter with the
      // dedicated /samples/queues/au-validation server-side endpoint, mirroring the
      // ICP Supervisor architecture. Backend now applies all BGM/multi-lab/AU-routing
      // filters in SQL with proper paging, killing the 20k truncation cliff and
      // dropping CPU pressure on the backend. The remaining client-side filter just
      // below is now defensive (server already filtered) and kept so the rest of the
      // dashboard logic that depends on auSamples shape continues to work unchanged.
      // Earlier comment about Option A (removing ICP_PREP_CHIMIQUE) is preserved
      // below for historical context — that array is no longer used.
      // const multiLabStatuses = [
      //   SampleStatus.VALIDATION_SERVICE,
      //   SampleStatus.LABORATOIRE_ICP_ANALYSE,
      //   SampleStatus.LABORATOIRE_AU_ANALYSE,
      //   SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE,
      //   SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE,
      // ];
      // Uses shared api client so Authorization picks up access_token on every
      // request (raw fetch + localStorage token was wrong — only access_token is
      // canonical; after refresh the dashboard went empty). See api.ts refreshToken().
      const response: any = await api.get('/samples/queues/au-validation', {
        params: {
          page: '1',
          limit: '1000',
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      });
      const allSamples = Array.isArray(response) ? response : (response.samples || []);
      let auSamples = allSamples.filter((sample: any) => {
        // Check for multi-lab samples (BGM OR custom routing)
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        
        // For multi-lab samples, show them if:
        // 1. AU analysis is complete AND AU validation is not done (needs validation)
        // 2. OR AU is validated but ICP is not (show with "En attente de validation ICP" label)
        if (isMultiLabSample) {
          const selectedElements = sample.metadonnees?.selected_elements || [];
          const elementRouting = sample.metadonnees?.element_lab_routing || {};
          // 🔧 FIX: Check if ANY selected element routes to AU (not just elementRouting values)
          const hasAuElements = selectedElements.some((element: string) => 
            elementRouting[element] === 'LABORATOIRE_AU'
          );
          // 🔧 FIX: Also check laboratories_involved as fallback
          const hasAuInLabs = sample.laboratories_involved?.includes('LABORATOIRE_AU') || false;
          const hasAuRouting = hasAuElements || hasAuInLabs || 
                               sample.metadonnees?.bgm_method_selected === true; // BGM always has AU
          
          const auAnalysisComplete = sample.metadonnees?.au_analysis_completed === true;
          const auValidationComplete = sample.metadonnees?.au_validation_completed === true;
          const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
          
          // Show if AU analysis is done and either:
          // - AU validation not done (needs validation)
          // - AU validated but ICP not validated (waiting for ICP)
          return hasAuRouting && auAnalysisComplete && (!auValidationComplete || (auValidationComplete && !icpValidationComplete));
        }
        
        // For non-multi-lab samples, only show if they have AU analysis data and are in VALIDATION_SERVICE
        return sample.statut === 'VALIDATION_SERVICE' &&
               sample.metadonnees?.au_analysis_form;
      });
      
      // 🔍 Apply search filter (frontend-side)
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        auSamples = auSamples.filter((sample: any) => 
          sample.sample_id?.toLowerCase().includes(searchLower) ||
          sample.metadonnees?.batch_number?.toLowerCase().includes(searchLower) ||
          sample.metadonnees?.batch_info?.batch_number?.toLowerCase().includes(searchLower) ||
          sample.reference_client?.toLowerCase().includes(searchLower)
        );
      }
      
      setSamples(auSamples as Sample[]);
      
      // Calculate stats
      const isMultiLab = (s: any) => s.metadonnees?.multi_lab_workflow === true || s.metadonnees?.bgm_method_selected === true;
      const pending = auSamples.filter((s: any) => 
        s.statut === 'VALIDATION_SERVICE' ||
        (isMultiLab(s) && s.metadonnees?.au_analysis_completed === true)
      ).length;
      const urgent = auSamples.filter((s: any) => s.priorite === 'URGENTE').length;

      setStats({
        pending_validation: pending,
        validated_today: 0,
        rejected_samples: 0,
        urgent_samples: urgent,
      });
      
    } catch (error) {
      console.error('Error loading AU validation data:', error);
      setError('Erreur lors du chargement des données de validation AU');
    } finally {
      setLoading(false);
    }
  };

  // Batch handling functions
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individual: Sample[] = [];

    samples.forEach(sample => {
      const batchNumber = getBatchNumber(sample);
      if (batchNumber) {
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individual.push(sample);
      }
    });

    return { batches, individual };
  };

  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || 
           sample.metadonnees?.batch_number || null;
  };

  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchNumber)) {
        newSet.delete(batchNumber);
      } else {
        newSet.add(batchNumber);
      }
      return newSet;
    });
  };

  // Initialize batch validation data
  const initializeBatchValidationData = (samples: Sample[]) => {
    const initialData: BatchValidationData = {};
    samples.forEach(sample => {
      initialData[sample.id] = {
        validationNotes: '',
        approved: true, // Default to approved
      };
    });
    setBatchValidationData(initialData);
  };

  // Update batch validation data
  const updateBatchValidationData = (sampleId: string, field: string, value: any) => {
    setBatchValidationData(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [field]: value
      }
    }));
  };

  // Handle batch validation start
  const handleStartBatchValidation = async (batchNumber: string, samples: Sample[]) => {
    setValidatingBatch(true);
    await new Promise(resolve => setTimeout(resolve, 50));
    setSelectedBatch({ batchNumber, samples });
    initializeBatchValidationData(samples);
    // Reset lot-level validation state
    setValidationDecision('APPROVE');
    setValidationNotes('');
    setBatchValidationDialog(true);
    setValidatingBatch(false);
  };

  // Complete batch validation
  const handleCompleteBatchValidation = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);
      
      // 🔥 CRITICAL FIX: Fetch FRESH sample data to avoid stale metadata issues
      // This prevents overwriting ICP validation data with stale frontend data
      console.log(`🔄 Fetching fresh sample data for batch ${selectedBatch.batchNumber}...`);
      const freshSamplesPromises = selectedBatch.samples.map(s => 
        samplesService.getSample(s.id).catch(err => {
          console.error(`Failed to fetch fresh data for ${s.sample_id}:`, err);
          return s; // Fallback to stale data if fetch fails
        })
      );
      const freshSamples = await Promise.all(freshSamplesPromises);
      console.log(`✅ Fetched fresh data for ${freshSamples.length} samples`);
      
        // Use LOT-level decision for ALL samples (not per-sample)
        const isApproved = validationDecision === 'APPROVE';
        
      // Prepare bulk update data with FRESH sample metadata
      const bulkUpdateData = freshSamples.map(sample => {
        const auValidationData = {
          validated_by: user?.id,
          validated_at: new Date().toISOString(),
          validation_notes: validationNotes,  // LOT-level notes
          validation_decision: isApproved ? 'APPROVED' : 'REJECTED',  // LOT-level decision
          batch_validation: true,
          batch_number: selectedBatch.batchNumber,
          supervisor_name: `${user?.firstName} ${user?.lastName}`,
          validation_source_laboratory: 'LABORATOIRE_AU',
        };

        // For MULTI-LAB samples (BGM or custom routing), check if ALL involved labs have completed validation
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                  sample.metadonnees?.bgm_method_selected === true;
        let newStatus;
        if (isApproved) {  // LOT-level approved
          if (isMultiLabSample) {
            // 🔥 CRITICAL: Check ALL involved labs from element_lab_routing, not just ICP
            const elementRouting = sample.metadonnees?.element_lab_routing || {};
            const involvedLabs = [...new Set(Object.values(elementRouting))] as string[];
            
            // Check if ALL involved labs (except AU which we're validating now) have completed validation
            const allOtherLabsValidated = involvedLabs.every(lab => {
              if (lab === 'LABORATOIRE_AU') return true; // We're validating AU now
              
              switch(lab) {
                case 'LABORATOIRE_SAA':
                  return sample.metadonnees?.saa_validation_completed === true ||
                         sample.metadonnees?.saa_validation?.validation_decision === 'APPROVED';
                case 'LABORATOIRE_ICP':
                  // 🔥 CRITICAL FIX: Remove generic validation_data check (causes false positives)
                  return sample.metadonnees?.icp_validation_completed === true ||
                         sample.metadonnees?.icp_validation?.validation_decision === 'APPROVED';
                case 'LABORATOIRE_VOLUMETRIE':
                  return sample.metadonnees?.volumetrie_validation_completed === true ||
                         sample.metadonnees?.volumetrie_validation?.validation_decision === 'APPROVED';
                case 'LABORATOIRE_PHYSICO_CHIMIQUE':
                  // 🔧 FIX: Added Physico-Chimique validation check
                  return sample.metadonnees?.physico_chimique_validated === true ||
                         sample.metadonnees?.physico_chimique_validation?.validation_decision === 'APPROVED';
                default:
                  // 🔧 FIX: Unknown labs should NOT be auto-validated
                  console.warn(`⚠️ Unknown lab ${lab} in AU validation check - treating as NOT validated`);
                  return false;
              }
            });
            
            // Only move to certificate generation if ALL labs are validated
            // Otherwise keep current status (will stay visible in AU Supervisor with "waiting other lab" indicator)
            newStatus = allOtherLabsValidated ? SampleStatus.GENERATION_CERTIFICAT : sample.statut;
            
            console.log(`🔍 AU Validation - Multi-lab sample ${sample.sample_id}:`, {
              isMultiLabSample,
              involvedLabs,
              allOtherLabsValidated,
              newStatus,
              currentStatus: sample.statut,
              icpValidated: sample.metadonnees?.icp_validation_completed,
              saaValidated: sample.metadonnees?.saa_validation_completed
            });
          } else {
            // Regular AU-only samples go directly to certificate generation
            newStatus = SampleStatus.GENERATION_CERTIFICAT;
          }
        } else {
          // LOT-level rejected - ALL samples go back to AU Analyst
          newStatus = SampleStatus.LABORATOIRE_AU_ANALYSE;
        }

        return {
          id: sample.id,
          sample_id: sample.sample_id,
        metadonnees: {
            ...sample.metadonnees,
            au_validation: auValidationData,
            au_validation_completed: isApproved,
            validation_completed_at: new Date().toISOString(),
            ...(hideWeightInCert ? { hide_weight_in_certificate: true } : {}),
          },
          status: newStatus,
          commentaires: isApproved 
            ? `Validation AU LOT terminée - Batch: ${selectedBatch.batchNumber}, Analyse AU validé`
            : `Validation AU rejetée - Retour analyse - Batch: ${selectedBatch.batchNumber}`
        };
      });

      // For batches with MULTI-LAB samples (BGM or custom routing), update each sample individually
      // This is critical because each sample may have a different target status depending on ICP validation state
      const hasMultiLabSamples = bulkUpdateData.some(item => {
        const sample = freshSamples.find(s => s.id === item.id);
        return sample?.metadonnees?.multi_lab_workflow === true || sample?.metadonnees?.bgm_method_selected === true;
      });
      
      let result;
      if (hasMultiLabSamples) {
        // 🔥 CRITICAL: Update samples individually to respect per-sample status
        // Multi-lab samples may need to wait for ICP validation before going to certificate
        console.log(`📝 Updating ${bulkUpdateData.length} multi-lab samples individually...`);
        const updatePromises = bulkUpdateData.map(async (updateData) => {
          try {
            console.log(`📤 Updating sample ${updateData.sample_id}:`, {
              status: updateData.status,
              au_validation_completed: updateData.metadonnees.au_validation_completed
            });
            await samplesService.updateSample(updateData.id, {
              metadonnees: updateData.metadonnees,
              statut: updateData.status,
              commentaires: updateData.commentaires
            });
            console.log(`✅ Sample ${updateData.sample_id} updated successfully`);
            return { success: true, id: updateData.id, sample_id: updateData.sample_id };
          } catch (error: any) {
            console.error(`❌ Failed to update sample ${updateData.sample_id}:`, error);
            return { success: false, id: updateData.id, sample_id: updateData.sample_id, error: error.message };
          }
        });
        
        const results = await Promise.all(updatePromises);
        const successCount = results.filter(r => r.success).length;
        const errors = results.filter(r => !r.success).map(r => `${r.sample_id}: ${r.error}`);
        result = { 
          updated: successCount, 
          errors 
        };
      } else {
        // Regular AU-only samples: use bulk update (all go to certificate)
        const sampleIds = bulkUpdateData.map(item => item.id);
        const metadataUpdates = Object.fromEntries(
          bulkUpdateData.map(item => [item.id, item.metadonnees])
        );
        const statusUpdate = {
          status: SampleStatus.GENERATION_CERTIFICAT,
          comment: 'Validation AU LOT terminée'
        };
        result = await samplesService.updateSampleBatch(sampleIds, metadataUpdates, statusUpdate);
      }

      // 🔔 Proper user feedback
      if (result.errors && result.errors.length > 0) {
        console.error(`❌ Validation errors:`, result.errors);
        setSnackbarSeverity('error');
        setSnackbarMessage(`⚠️ Erreurs lors de la validation: ${result.errors.join(', ')}`);
        setSnackbarOpen(true);
      } else if (result.updated === 0) {
        setSnackbarSeverity('warning');
        setSnackbarMessage(`⚠️ Aucun échantillon mis à jour. Veuillez réessayer.`);
        setSnackbarOpen(true);
      } else {
        // Check if samples moved to certificate or are waiting for other labs
        const movedToCertificate = bulkUpdateData.filter(d => d.status === SampleStatus.GENERATION_CERTIFICAT).length;
        const waitingOtherLabs = bulkUpdateData.filter(d => d.status !== SampleStatus.GENERATION_CERTIFICAT && d.status !== SampleStatus.LABORATOIRE_AU_ANALYSE).length;
        
        if (movedToCertificate > 0 && waitingOtherLabs === 0) {
          setSnackbarSeverity('success');
          setSnackbarMessage(`✅ Lot ${selectedBatch.batchNumber} validé! ${result.updated} échantillons envoyés en génération de certificat.`);
        } else if (waitingOtherLabs > 0) {
          setSnackbarSeverity('info');
          setSnackbarMessage(`✅ Validation AU terminée pour ${result.updated} échantillons. ${waitingOtherLabs} en attente de validation des autres laboratoires.`);
        } else {
          setSnackbarSeverity('success');
          setSnackbarMessage(`✅ Lot ${selectedBatch.batchNumber} ${isApproved ? 'approuvé' : 'rejeté'} avec succès! ${result.updated} échantillons mis à jour.`);
        }
        setSnackbarOpen(true);
      }
      
      setBatchValidationDialog(false);
      setSelectedBatch(null);
      setBatchValidationData({});
      loadAUValidationData();
      
    } catch (error: any) {
      console.error('Error completing batch validation:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(`❌ Erreur lors de la validation du lot: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePartialCertificate = async () => {
    if (!selectedBatch) return;
    try {
      setGeneratingPartialCert(true);
      const sampleIds = selectedBatch.samples.map(s => s.id);
      const result = await samplesService.generatePartialBatchCertificate(sampleIds, 'LABORATOIRE_AU');
      setSnackbarSeverity('success');
      setSnackbarMessage(`Certificat partiel AU généré: ${result.data?.certificate_number || 'OK'}`);
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Error generating AU partial certificate:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(`Erreur génération certificat partiel AU: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setGeneratingPartialCert(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données de validation AU...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadAUValidationData} sx={{ ml: 2 }}>
          Réessayer
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <VerifiedUser sx={{ fontSize: '2rem', color: '#e65100' }} />
          🔍 Validation & Supervision AU - {user?.firstName} {user?.lastName}
        </Typography>
        
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {stats.pending_validation}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  En attente validation
                  </Typography>
            </CardContent>
          </Card>
        </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffebee', border: '1px solid #ef5350' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {stats.urgent_samples}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  Urgents
                  </Typography>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
              </Box>

      {/* 🔍 SEARCH FIELD */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Rechercher par N° Lot ou N° Échantillon..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ 
            width: 400,
            backgroundColor: 'white',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {debouncedSearch && (
          <Chip 
            label={`Recherche: "${debouncedSearch}"`}
            onDelete={() => setSearchTerm('')}
            color="primary"
            variant="outlined"
          />
        )}
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadAUValidationData}
          disabled={loading}
        >
          Actualiser
        </Button>
              </Box>

      {/* Samples List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUser />
            🔍 Échantillons pour Validation AU ({samples.length})
          </Typography>
          
          {samples.length === 0 ? (
            <Box textAlign="center" py={4}>
              <VerifiedUser sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun échantillon en attente de validation AU
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Les échantillons apparaîtront ici après l'analyse Analyse AU.
            </Typography>
          </Box>
          ) : (
            <>
            <TableContainer>
              <Table>
              <TableHead>
                <TableRow>
                    <TableCell>Échantillon</TableCell>
                  <TableCell>Client</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Résultats AU</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {(() => {
                    const { batches, individual } = groupSamplesByBatch(samples);
                    const items: Array<{type: 'batch', batchNumber: string, samples: Sample[]} | {type: 'individual', sample: Sample}> = [];
                    
                    Object.entries(batches).forEach(([batchNumber, batchSamples]) => {
                      items.push({ type: 'batch', batchNumber, samples: batchSamples });
                    });
                    individual.forEach(sample => {
                      items.push({ type: 'individual', sample });
                    });

                    // 🚀 Apply frontend pagination
                    const paginatedItems = items.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
                    
                    // Store total for pagination controls
                    if (items.length !== totalBatches) {
                      setTimeout(() => setTotalBatches(items.length), 0);
                    }

                    return paginatedItems.map((item, index) => {
                      if (item.type === 'batch') {
                        const isExpanded = expandedBatches.has(item.batchNumber);
                  
                  return (
                          <React.Fragment key={item.batchNumber}>
                            <TableRow sx={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #e65100' }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => toggleBatchExpansion(item.batchNumber)}
                                  >
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                  <Box>
                                    <Box sx={{ 
                                      backgroundColor: '#e65100', 
                                      color: 'white', 
                                      px: 1, 
                                      py: 0.5, 
                                      borderRadius: 1,
                                      fontWeight: 'bold'
                                    }}>
                                      LOT AU VALIDATION
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                                      {item.batchNumber}
                                    </Typography>
                                    <Chip 
                                      label={`${item.samples.length} échantillons`} 
                                      size="small" 
                                      color="warning" 
                                      variant="outlined"
                                    />
                                    
                                    {/* 🔍 BGM STATUS INDICATOR - VISUAL ONLY (AU PERSPECTIVE) */}
                                    {(() => {
                                      const bgmStatus = getBGMValidationStatus(item.samples);
                                      if (bgmStatus.isBGM && bgmStatus.awaitingICP) {
                                        return (
                                          <Chip 
                                            label="⏳ EN ATTENTE DE VALIDATION ICP" 
                                            sx={{ 
                                              fontWeight: 'bold',
                                              fontSize: '0.8rem',
                                              px: 1.5,
                                              py: 0.5,
                                              mt: 0.5,
                                              background: `linear-gradient(45deg, #2196f3, #03a9f4)`,
                                              color: 'white',
                                              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.4)',
                                              animation: 'pulse 2s infinite',
                                              '@keyframes pulse': {
                                                '0%': { opacity: 1 },
                                                '50%': { opacity: 0.7 },
                                                '100%': { opacity: 1 }
                                              }
                                            }}
                                          />
                                        );
                                      } else if (bgmStatus.isBGM && bgmStatus.icpCompleted) {
                                        return (
                                          <Chip 
                                            label="✅ BGM COMPLET (AU + ICP)" 
                                            sx={{ 
                                              fontWeight: 'bold',
                                              fontSize: '0.8rem',
                                              px: 1.5,
                                              py: 0.5,
                                              mt: 0.5,
                                              background: `linear-gradient(45deg, #4caf50, #8bc34a)`,
                                              color: 'white',
                                              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                                            }}
                                          />
                                        );
                                      } else if (bgmStatus.isBGM) {
                                        return (
                                          <Chip 
                                            label="🔬 ÉCHANTILLON BGM" 
                                            sx={{ 
                                              fontWeight: 'bold',
                                              fontSize: '0.8rem',
                                              px: 1.5,
                                              py: 0.5,
                                              mt: 0.5,
                                              background: `linear-gradient(45deg, #9c27b0, #e91e63)`,
                                              color: 'white',
                                              boxShadow: '0 2px 8px rgba(156, 39, 176, 0.4)',
                                            }}
                                          />
                                        );
                                      }
                                      return null;
                                    })()}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>{item.samples[0]?.client?.company_name || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.samples.some(s => s.priorite === Priority.URGENTE) ? 'URGENTE' : 'NORMALE'}
                                  size="small"
                                  color={item.samples.some(s => s.priorite === Priority.URGENTE) ? 'error' : 'default'}
                                />
                              </TableCell>
                      <TableCell>
                        <Chip
                                  label="AU + Ag Analyse AU" 
                          size="small"
                                  color="success" 
                                  variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={validatingBatch ? <CircularProgress size={16} /> : <VerifiedUser />}
                                  disabled={validatingBatch}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleStartBatchValidation(item.batchNumber, item.samples);
                                  }}
                                  sx={{ ml: 'auto' }}
                                >
                                  {validatingBatch ? 'Préparation...' : 'Valider le Lot'}
                                </Button>
                              </TableCell>
                            </TableRow>

                            {/* Individual Sample Rows - Only show when expanded */}
                            {isExpanded && item.samples.map((sample, sampleIndex) => (
                              <TableRow 
                                key={sample.id}
                                sx={{ 
                                  backgroundColor: '#fff8f0',
                                  borderLeft: '2px solid #e65100'
                                }}
                              >
                                <TableCell sx={{ pl: 4 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                                      {sample.sample_id}
                        </Typography>
                                    <Chip 
                                      label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </Box>
                      </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                                    label={sample.priorite}
                          size="small"
                                    color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                                  {sample.metadonnees?.au_analysis_form?.analysis_readings?.length || 0} éléments
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<VerifiedUser />}
                                    onClick={() => {
                                      setSelectedSample(sample);
                                      setValidationDialog(true);
                                    }}
                                  >
                                    Valider Individuel
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      } else {
                        // Individual sample
                        const sample = item.sample;
                        return (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {sample.sample_id}
                              </Typography>
                            </TableCell>
                            <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={sample.priorite}
                                size="small"
                                color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                              />
                      </TableCell>
                      <TableCell>
                              {sample.metadonnees?.au_analysis_form?.analysis_readings?.length || 0} éléments
                      </TableCell>
                      <TableCell>
                              <Button
                            size="small"
                                variant="outlined"
                                startIcon={<VerifiedUser />}
                                onClick={() => {
                                  setSelectedSample(sample);
                                  setValidationDialog(true);
                                }}
                              >
                                Valider
                              </Button>
                      </TableCell>
                    </TableRow>
                  );
                      }
                    });
                  })()}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* 🚀 PAGINATION CONTROLS */}
          <TablePagination
            component="div"
            count={totalBatches}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100, 250]}
            labelRowsPerPage="Lots par page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`} lots`
            }
            sx={{
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f5f5f5',
            }}
          />
          </>
          )}
        </CardContent>
      </Card>

      {/* Batch Validation Dialog */}
      <Dialog 
        open={batchValidationDialog} 
        onClose={() => setBatchValidationDialog(false)} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#e65100',
              color: 'white'
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <VerifiedUser sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" fontWeight="bold">
                Validation AU Analyse AU - Lot {selectedBatch?.batchNumber}
              </Typography>
              <Chip 
                label={`${selectedBatch?.samples.length || 0} échantillons`} 
                color="warning" 
                variant="filled"
                sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
              />
            </Box>
            
            {/* 🔍 BGM STATUS INFO IN DIALOG - VISUAL ONLY (AU PERSPECTIVE) */}
            {selectedBatch && (() => {
              const bgmStatus = getBGMValidationStatus(selectedBatch.samples);
              if (bgmStatus.isBGM) {
                return (
                  <Typography variant="body2" sx={{ 
                    color: 'white', 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                    px: 2, 
                    py: 0.5, 
                    borderRadius: 1,
                    fontWeight: 'bold'
                  }}>
                    🔬 <strong>Échantillon Multi-Lab:</strong> {
                      bgmStatus.awaitingICP
                        ? '⏳ Validation AU terminée - En attente de validation ICP pour génération de certificat'
                        : bgmStatus.icpCompleted
                          ? '✅ Validations AU et ICP terminées - Prêt pour certificat'
                          : '🔄 En cours de validation AU'
                    }
                  </Typography>
                );
              }
              return null;
            })()}
            
            <Button
              onClick={() => setBatchValidationDialog(false)}
              sx={{ color: 'white', minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              🔍 <strong>Validation des résultats Analyse AU AU</strong> - Vérifiez les concentrations et approuvez le lot pour génération de certificat
            </Alert>

            {/* Batch Validation Table */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    🔍 Validation Lot Analyse AU - {selectedBatch?.batchNumber}
                  </Typography>
                  
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {/* 🔧 FIX: Use ALL batch elements (union) for header chips */}
                    {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => (
                      <Chip 
                        key={element} 
                        label={element} 
                        size="small"
                        color="warning" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                      />
                    ))}
                  </Box>
                </Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                      <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#e65100', color: 'white', fontSize: '0.8rem' } }}>
                        <TableCell>Échantillon</TableCell>
                        <TableCell sx={{ backgroundColor: '#ff8a65' }}>Facteur Dilution</TableCell>
                        {/* 🔧 FIX: Use ALL batch elements (union) for table headers */}
                        {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => (
                          <React.Fragment key={element}>
                            <TableCell sx={{ backgroundColor: '#e8ffe8', borderLeft: '2px solid #4caf50' }}>
                              {element} (Lecture)
                            </TableCell>
                            <TableCell sx={{ backgroundColor: '#fff9c4', borderLeft: '2px solid #fbc02d' }}>
                              {element} (g/t)
                            </TableCell>
                          </React.Fragment>
                        ))}
                        <TableCell>Notes Validation</TableCell>
                        <TableCell>Approuvé</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {selectedBatch?.samples.map((sample, index) => {
                        const analysisReadings = sample.metadonnees?.au_analysis_form?.analysis_readings || [];
                        const prepData = sample.metadonnees?.au_preparation || {};
                        const validationData = batchValidationData[sample.id] || { validationNotes: '', approved: true };
                        const elementsForSample = getElementsForSample(sample);
                        
                        // Get AU method code for detection limits
                        const auMethodCode = sample.metadonnees?.analyses_demandees?.find((method: string) => 
                          method.includes('Au-PRE.MO/ANA')
                        ) || 'Au-PRE.MO/ANA/001 FA AA';
                        
                        return (
                          <TableRow 
                            key={sample.id}
                            sx={{ backgroundColor: index % 2 === 0 ? '#fff8f0' : 'white' }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {sample.sample_id}
                              </Typography>
                            </TableCell>
                            
                            {/* Dilution Factor */}
                            <TableCell sx={{ backgroundColor: '#ffe0cc' }}>
                              <Chip 
                                label={`${prepData.totalDilutionFactor || prepData.total_dilution_factor || prepData.dilution_factor || 1}x`}
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            
                            {/* 🔧 FIX: Iterate over ALL batch elements, grey out non-selected */}
                            {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => {
                              const hasElement = elementsForSample.includes(element);
                              
                              // 🔧 FIX: Show greyed cells for elements not in this sample
                              if (!hasElement) {
                                return (
                                  <React.Fragment key={element}>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', borderLeft: '1px solid #4caf50' }}>
                                      <Typography sx={{ color: '#bdbdbd', fontSize: '0.75rem', textAlign: 'center' }}>-</Typography>
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', borderLeft: '1px solid #fbc02d' }}>
                                      <Typography sx={{ color: '#bdbdbd', fontSize: '0.75rem', textAlign: 'center' }}>-</Typography>
                                    </TableCell>
                                  </React.Fragment>
                                );
                              }
                              
                              const result = analysisReadings.find((r: any) => r.element === element);
                              const lecture = result?.absorbance || result?.grams_per_tonne || 0;
                              const concentration = result?.grams_per_tonne || 0;
                              
                              // Method-specific detection limit for AU
                              const detectionLimit = element === 'Au' ? 0.05 : 1; // Au: 0.05 g/t, Ag: 1 g/t
                              
                              return (
                                <React.Fragment key={element}>
                                  {/* Lecture */}
                                  <TableCell sx={{ backgroundColor: '#f1f8f1', borderLeft: '1px solid #4caf50' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: lecture > 0 ? '#2e7d32' : '#999' }}>
                                      {lecture > 0 ? lecture.toFixed(4) : 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                  {/* Concentration with detection limit */}
                                  <TableCell sx={{ backgroundColor: '#fffde7', borderLeft: '1px solid #fbc02d' }}>
                                    <Chip
                                      label={
                                        concentration < detectionLimit 
                                          ? `< ${detectionLimit}` 
                                          : concentration.toFixed(3)
                                      }
                                      size="small"
                                      color={concentration >= detectionLimit ? 'success' : 'warning'}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </TableCell>
                                </React.Fragment>
                              );
                            })}
                            <TableCell>
                              <TextField
                                size="small"
                                multiline
                                rows={1}
                                value={validationData.validationNotes}
                                onChange={(e) => updateBatchValidationData(sample.id, 'validationNotes', e.target.value)}
                                placeholder="Notes de validation..."
                                sx={{ width: '200px' }}
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small">
                                <Select
                                  value={validationData.approved ? 'approved' : 'rejected'}
                                  onChange={(e) => updateBatchValidationData(sample.id, 'approved', e.target.value === 'approved')}
                                  sx={{ width: '120px' }}
                                >
                                  <MenuItem value="approved">✅ Approuvé</MenuItem>
                                  <MenuItem value="rejected">❌ Rejeté</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      </TableBody>
                    </Table>
                </TableContainer>

                {/* Validation Summary */}
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: '#e8f5e8', 
                  borderRadius: 1, 
                  border: '1px solid #4caf50'
                }}>
                  <Typography variant="h6" color="success.main" gutterBottom sx={{ fontSize: '0.9rem' }}>
                    📊 Résumé de Validation
                  </Typography>
                  <Box display="flex" justifyContent="space-around" flexWrap="wrap" gap={2}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Approuvés</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.filter(sample => 
                          batchValidationData[sample.id]?.approved !== false
                        ).length || 0}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Rejetés</Typography>
                      <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.filter(sample => 
                          batchValidationData[sample.id]?.approved === false
                        ).length || 0}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.length || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                  </CardContent>
                </Card>

                {/* LOT-LEVEL VALIDATION DECISION (like ICP Supervisor) */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #ddd' }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#e65100' }}>
                    📝 Décision de Validation du Lot
                  </Typography>
                  
                  {/* Validation Decision Radio Buttons */}
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      value={validationDecision}
                      onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT')}
                      row
                    >
                      <FormControlLabel
                        value="APPROVE"
                        control={<Radio color="success" />} 
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Approuver le Lot
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="REJECT"
                        control={<Radio color="error" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Cancel color="error" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Rejeter le Lot
                            </Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideWeightInCert}
                        onChange={(e) => setHideWeightInCert(e.target.checked)}
                        color="warning"
                      />
                    }
                    label="Masquer le poids dans le certificat final"
                    sx={{ mt: 1, mb: 1, display: 'block' }}
                  />
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Notes de validation (OBLIGATOIRES)"
                    value={validationNotes}
                    onChange={(e) => setValidationNotes(e.target.value)}
                    placeholder="Commentaires obligatoires sur la qualité des analyses AU, conformité des résultats, actions correctives si nécessaire..."
                    required
                    error={!validationNotes.trim()}
                    helperText={!validationNotes.trim() ? "Les notes de validation sont obligatoires pour valider le lot" : "Notes sauvegardées automatiquement"}
                    sx={{ 
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: !validationNotes.trim() ? '#d32f2f' : '#e65100'
                        }
                      }
                    }}
                  />
                </Box>
            </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: 'white', borderTop: '1px solid #ddd' }}>
          <Button
            onClick={() => setBatchValidationDialog(false)}
            color="inherit"
          >
            Annuler
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={generatingPartialCert ? <CircularProgress size={16} /> : <Assessment />}
            onClick={handleGeneratePartialCertificate}
            disabled={generatingPartialCert}
            sx={{ fontSize: '0.8rem' }}
          >
            {generatingPartialCert ? 'Génération en cours...' : 'Certificat Partiel AU'}
          </Button>
          <Button
            variant="contained"
            color={validationDecision === 'APPROVE' ? 'success' : 'error'}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
            disabled={loading || !selectedBatch?.samples.length || !validationNotes.trim()}
            onClick={handleCompleteBatchValidation}
            title={!validationNotes.trim() ? "Veuillez saisir des notes de validation" : ""}
          >
            {loading 
              ? 'Validation...' 
              : validationDecision === 'APPROVE'
                ? `✅ Approuver le Lot (${selectedBatch?.samples.length || 0} échantillons)`
                : `❌ Rejeter le Lot (${selectedBatch?.samples.length || 0} échantillons)`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔔 Snackbar for user feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%', fontSize: '1rem' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AUSupervisorDashboard; 